import { useCallback, useContext, useState } from 'react';

import { FilterContextData } from 'contexts/FilterContext';
import { usePpaQueryFilters } from 'hooks/ppa/usePpaQueryFilters';
import { downloadCsv, sendNotification } from 'utils/helperMethods';
import { downloadExcel } from 'utils/ppaHelpers';

import type { PricePackBubbleRow, PricePackArchitectureRequest } from 'hooks/ppa/useFetchPricePackArchitecture';
import type { PackArchitectureKpiIdMatcher } from 'components/pages/PackArchitecture/packArchitectureKpiIdMatching.helpers';
import { buildPackArchitectureGlobalFiltersSummary } from './packArchitectureGlobalFilters.helpers';
import { AXIS_PREVIEW_SALES_KPI_IDS } from './packArchitectureMeta.helpers';
import { showPackArchitectureExportSuccessNotification } from './packArchitectureExportNotification';
import {
    buildPackArchitectureNormalizedExportRows,
    fetchPackArchitectureExportRows,
    formatPackArchitectureExportRowsForCsv,
    type PackArchitectureExportFormat,
    type PackArchitectureExportScope,
} from './packArchitectureExport.helpers';

type UsePackArchitectureExportParams = {
    chartTitle: string;
    xAxisLabel: string;
    yAxis: string;
    yAxisLabel: string;
    bubbleRepresentsLabel: string;
    bubbleSizeLabel: string;
    showHeatMap: boolean;
    kpiIdMatcher: PackArchitectureKpiIdMatcher;
    rows: PricePackBubbleRow[];
    chartRequest: PricePackArchitectureRequest | null;
    totalCount: number;
    onExportSuccess?: () => void;
};

const buildExportFileName = () => {
    const now = new Date();
    const date = now.toISOString().slice(0, 10);
    const time = now.toTimeString().slice(0, 8).replace(/:/g, '-');
    return `price-pack-architecture_${date}_${time}`;
};

const formatExportDateTime = (dateTime: Date) => {
    const month = dateTime.getMonth() + 1;
    const day = dateTime.getDate();
    const year = dateTime.getFullYear();
    const hours24 = dateTime.getHours();
    const minutes = String(dateTime.getMinutes()).padStart(2, '0');
    const seconds = String(dateTime.getSeconds()).padStart(2, '0');
    const period = hours24 >= 12 ? 'PM' : 'AM';
    const hours12 = hours24 % 12 || 12;

    return `${month}/${day}/${year}  ${hours12}:${minutes}:${seconds} ${period}`;
};

const buildExportMetadataRows = ({
    chartTitle,
    xAxisLabel,
    yAxisLabel,
    bubbleRepresentsLabel,
    bubbleSizeLabel,
    showHeatMap,
    globalFiltersValue,
}: {
    chartTitle: string;
    xAxisLabel: string;
    yAxisLabel: string;
    bubbleRepresentsLabel: string;
    bubbleSizeLabel: string;
    showHeatMap: boolean;
    globalFiltersValue: string;
}): Array<Array<string | number>> => {
    const exportDate = formatExportDateTime(new Date());
    const exportedBy = localStorage.getItem('email') ?? '-';

    return [
        ['Global filters', globalFiltersValue || '-'],
        ['X-Axis', xAxisLabel || '-'],
        ['Y-Axis', yAxisLabel || '-'],
        ['Bubble Represents', bubbleRepresentsLabel || '-'],
        ['Bubble Size', bubbleSizeLabel || '-'],
        ['Growth Heatmap', showHeatMap ? 'ON' : 'OFF'],
        [],
        ['Chart Title', chartTitle],
        ['Export Date', exportDate],
        ['Exported By', exportedBy],
    ];
};

export const usePackArchitectureExport = ({
    chartTitle,
    xAxisLabel,
    yAxis,
    yAxisLabel,
    bubbleRepresentsLabel,
    bubbleSizeLabel,
    showHeatMap,
    kpiIdMatcher,
    rows,
    chartRequest,
    totalCount,
    onExportSuccess,
}: UsePackArchitectureExportParams) => {
    const {
        allCheckedAttribute,
        ppaPersistedScope,
        timeSelectionLabel,
        timePeriodList,
    } = useContext(FilterContextData);
    const { timePeriodLabel: resolvedTimePeriodLabel } = usePpaQueryFilters();
    const [isExporting, setIsExporting] = useState(false);

    const handleExport = useCallback(
        async ({ dataScope, format }: { dataScope: PackArchitectureExportScope; format: PackArchitectureExportFormat }) => {
            if (!chartRequest) {
                sendNotification('info', 'No data available to export.');
                return;
            }

            setIsExporting(true);
            try {
                const exportRowsData = await fetchPackArchitectureExportRows({
                    scope: dataScope,
                    chartRequest,
                    currentRows: rows,
                    totalCount,
                });

                if (exportRowsData.length === 0) {
                    sendNotification('info', 'No data available to export.');
                    return;
                }

                const globalFiltersValue = buildPackArchitectureGlobalFiltersSummary({
                    timeSelectionLabel: timeSelectionLabel || resolvedTimePeriodLabel,
                    chartRequest,
                    timePeriodList,
                    ppaPersistedScope,
                    allCheckedAttribute,
                });

                const metadataRows = buildExportMetadataRows({
                    chartTitle,
                    xAxisLabel,
                    yAxisLabel,
                    bubbleRepresentsLabel,
                    bubbleSizeLabel,
                    showHeatMap,
                    globalFiltersValue,
                });

                const exportResult = buildPackArchitectureNormalizedExportRows({
                    rows: exportRowsData,
                    yAxis,
                    yAxisLabel,
                    xAxisLabel: xAxisLabel || 'X-Axis',
                    bubbleRepresentsLabel: bubbleRepresentsLabel || 'Segment',
                    matchesKpiId: kpiIdMatcher.matches,
                    valueSalesKpiId: AXIS_PREVIEW_SALES_KPI_IDS.valueSales,
                    volumeSalesKpiId: AXIS_PREVIEW_SALES_KPI_IDS.volumeSales,
                    metadataRows,
                });

                const fileName = buildExportFileName();
                const fileNameWithExt = `${fileName}.${format}`;

                if (format === 'xlsx') {
                    await downloadExcel(fileName, exportResult.rows, {
                        sheetName: 'Price Pack Architecture',
                        columnFormats: exportResult.columnFormats,
                        // Ensure large KPI numbers are fully visible in Excel (avoid ####### cells).
                        columnWidths: [24, 20, 16, 20, 24, 20, 30],
                        dataStartRow: exportResult.headerRowIndex + 1,
                    });
                } else {
                    const csvRows = formatPackArchitectureExportRowsForCsv(
                        exportResult.rows,
                        exportResult.columnFormats,
                        exportResult.headerRowIndex,
                    );
                    const csvEscape = (value: string | number) => {
                        const str = String(value ?? '');
                        return /[",\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
                    };
                    const bom = '\uFEFF';
                    const csvContent = csvRows.map((row) => row.map(csvEscape).join(',')).join('\n');
                    downloadCsv([bom + csvContent], fileNameWithExt);
                }

                showPackArchitectureExportSuccessNotification(dataScope);
                onExportSuccess?.();
            } catch {
                sendNotification('error', 'Failed to export data. Please try again.');
            } finally {
                setIsExporting(false);
            }
        },
        [
            allCheckedAttribute,
            bubbleRepresentsLabel,
            bubbleSizeLabel,
            chartRequest,
            chartTitle,
            kpiIdMatcher.matches,
            ppaPersistedScope,
            resolvedTimePeriodLabel,
            rows,
            showHeatMap,
            timePeriodList,
            timeSelectionLabel,
            totalCount,
            xAxisLabel,
            yAxis,
            yAxisLabel,
            onExportSuccess,
        ],
    );

    return { isExporting, handleExport };
};
